import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { KEY_CODES } from '@editor/global/keycodes';
import analytics from '@repo/analytics';
import { useDebounce } from '@repo/react-hooks/useDebounce';
import { Button, IconButton } from '@repo/ui/components/Buttons';
import { IconEventWrapper, IconSwitcher } from '@repo/ui/components/Icon';
import EyeOutlineIcon from '@repo/ui/components/Icon/EyeOutlineIcon';
import EyeOutlineSlashIcon from '@repo/ui/components/Icon/EyeOutlineSlashIcon';
import InfoThickIcon from '@repo/ui/components/Icon/InfoThickIcon';
import { useNewThemeActive } from '@repo/ui/theming';

import { TOGGLE_PASSWORD } from '../../global/analytics/events';
import Tooltip from '../Tooltip/Tooltip';

import {
  ButtonWrapper,
  ContentWrapper,
  IconWrapper,
  InfoText,
  InputBox,
  InputContainer,
  InputField,
  InputWrapper,
  Label,
  RightIcons,
} from './styles';
import { defaultTheme, newTheme } from './theme';
import { TextInputElement, TextInputProps, TextInputTheme } from './types';

const DEFAULT_DEBOUNCE = 2000;

const stopPropagation = (event: React.SyntheticEvent<TextInputElement>): void =>
  event.stopPropagation();
const preventDefault = (event: React.SyntheticEvent<TextInputElement>): void =>
  event.preventDefault();

const getIconColor = (
  color: string,
  theme: TextInputTheme,
  error?: string
): string => {
  if (error) {
    return theme.colorError;
  }
  return color;
};

type SelectionRange = [start: number, end: number];

// Return the selection range moved back for the inputted letter
const getSelectionRange = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  offset: number
): SelectionRange => {
  const { target } = event;
  return [
    Math.max((target.selectionStart || 0) - offset, 0),
    Math.max((target.selectionEnd || 0) - offset, 0),
  ];
};

const TextInput: React.FC<TextInputProps> = ({
  useInput = true,
  disabled = false,
  activeOnHover = true,
  activeOnFocus = true,
  maxLength,
  placeholder = '',
  onChanging,
  onChanged,
  onFocus,
  onBlur,
  onEnter,
  blurOnEnter = true,
  onKeyDown,
  pattern,
  dataTestId = 'text-input',
  trimPasteToMaxLength = false,
  ...props
}) => {
  const isNewThemeActive = useNewThemeActive();

  const debounceTime = props.debounceTime || DEFAULT_DEBOUNCE;
  const theme = {
    ...defaultTheme,
    ...(isNewThemeActive ? newTheme : {}),
    ...props.theme,
  };
  const iconTheme = {
    color: getIconColor(theme.iconColor || theme.color, theme, props.error),
    activeDefaultColor: getIconColor(
      theme.iconColorActive || theme.iconColor || theme.colorActive,
      theme,
      props.error
    ),
    activeColor: getIconColor(
      theme.iconColorActive || theme.iconColor || theme.colorActive,
      theme,
      props.error
    ),
    transition: theme.transition,
  };

  const [currentValue, setCurrentValue] = useState(props.value || '');
  const [key, setKey] = useState(false); // key is used to force update contentEditable field

  const [showPassword, setShowPassword] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const error = useMemo(() => {
    return props.error;
  }, [props.error]);

  const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement>();

  const setRef = (node: HTMLInputElement): void => {
    inputRef.current = node;
    if (props.inputRef) {
      if (typeof props.inputRef === 'function') {
        props.inputRef(node);
      } else {
        props.inputRef.current = node;
      }
    }
  };

  useEffect(() => {
    if (inputRef.current && selectionRange?.length) {
      inputRef.current.setSelectionRange(...selectionRange);
    }
  }, [selectionRange]);

  useEffect(() => {
    // if a value is set and editing is left, use the given value
    if (props.value !== undefined && !isEditing) {
      setCurrentValue(props.value || '');
    }
  }, [props.value, isEditing]);

  const onChangedDebounced = useDebounce({
    callback: (val: string) => {
      props.onDebounce?.(val);
    },
    delay: debounceTime,
  });

  const getValue = useCallback(
    (
      event:
        | React.KeyboardEvent<TextInputElement>
        | React.ChangeEvent<TextInputElement>
        | React.FocusEvent<TextInputElement>
        | React.ClipboardEvent
    ) => {
      let value = '';

      if (
        useInput &&
        (event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement)
      ) {
        value = event.target.value;
      } else if (event.target instanceof HTMLDivElement) {
        value = event.target.innerText.trim() || placeholder || '';
      } else if (event instanceof ClipboardEvent) {
        value = event.currentTarget.innerHTML;
      }

      return value;
    },
    [useInput, placeholder]
  );

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newValue = getValue(event);

    if (newValue && pattern && !pattern.test(newValue)) {
      // Prevent moving cursor from the current position
      return setSelectionRange(
        getSelectionRange(event, newValue.length - currentValue.length)
      );
    }

    setCurrentValue(newValue);
    onChanging?.(newValue);
    onChangedDebounced(newValue);
  };

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    setIsEditing(true);
    onFocus?.(e);
  };

  const onInputBlur = (event: React.FocusEvent<TextInputElement>): void => {
    const newValue = getValue(event);

    if (newValue && pattern && !pattern.test(newValue)) {
      return;
    }

    setCurrentValue(newValue);
    setIsEditing(false);
    !useInput && setKey((prev) => !prev); // key is used to force update contentEditable field
    onChanged?.(newValue);
    onBlur?.();
  };

  const onInputKeyDown = (
    event: React.KeyboardEvent<TextInputElement>
  ): void => {
    event.stopPropagation();
    if (
      event.code === KEY_CODES.ENTER ||
      event.code === KEY_CODES.NUMPAD_ENTER ||
      event.key === 'Enter'
    ) {
      setIsEditing(false);
      onEnter?.(getValue(event));
      if (blurOnEnter && event.target instanceof HTMLElement) {
        event.target.blur(); // blur input, triggers `onInputBlur`
      }
    }
    onKeyDown?.(event);
  };

  const handleEditableInput = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    // If user presses enter, blur the input
    if (event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
      return;
    }

    const isMaxLength = maxLength && getValue(event).length >= maxLength;
    if (isMaxLength) {
      // Contents are already at the maximum length allowed

      const selection = window.getSelection()?.toString();
      // If we don't have a range selected, only allow backspace
      if (
        (!selection || !selection.length) &&
        event.code !== 'Backspace' &&
        event.code !== 'ArrowLeft' &&
        event.code !== 'ArrowRight' &&
        event.code !== 'Delete'
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const onPaste = (event: React.ClipboardEvent): void => {
    const data = event.clipboardData.getData('text/plain');
    if (data && maxLength) {
      const selectionLength = window.getSelection()?.toString().length ?? 0;
      const currentContentLength = getValue(event).length;
      const copiedContentLength = data.length;
      if (trimPasteToMaxLength) {
        const remainingLength =
          maxLength - currentContentLength + selectionLength;
        if (copiedContentLength > remainingLength) {
          event.preventDefault();
          event.stopPropagation();
          const trimmedData = data.substring(0, remainingLength);
          document.execCommand('insertText', false, trimmedData);
        }
      } else if (
        currentContentLength - selectionLength + copiedContentLength >
        maxLength
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((previous) => {
      analytics.track(TOGGLE_PASSWORD, {
        label: `hidden: ${previous}`,
      });
      return !previous;
    });
  }, []);

  const inputName = props.name ?? props.label;

  const inputProps = {
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    onKeyDown: onInputKeyDown,
    onPaste,
    disabled,
    isPasswordInput: props.isPasswordInput,
    isTextArea: props.isTextArea,
    autoComplete: props.autoComplete,
    name: inputName,
    error: props.error,
    rows: props.rows,
  };

  return (
    <InputWrapper
      data-testid={dataTestId}
      theme={theme}
      onClick={stopPropagation}
    >
      {props.label && (
        <Label
          as="label"
          data-testid="text-input--label"
          error={!!error}
          htmlFor={inputName}
          theme={theme}
        >
          {props.label}
        </Label>
      )}
      <InputContainer
        disabled={disabled}
        error={!!props.error}
        isActive={props.isActive}
        isNewThemeActive={isNewThemeActive}
        theme={theme}
      >
        <ContentWrapper>
          <IconEventWrapper
            activeOnFocus={activeOnFocus}
            activeOnHover={activeOnHover}
            disabled={disabled}
            fitParent={true}
            theme={iconTheme}
          >
            {props.icon && !props.iconAtEnd && (
              <Tooltip
                align={props.tooltipAlignment}
                placement={props.tooltipPlacement}
                text={props.tooltipText}
              >
                <IconWrapper theme={theme}>
                  <IconSwitcher
                    disabled={disabled}
                    height={theme.iconHeight}
                    icon={props.icon}
                    isActive={props.isActive}
                    theme={iconTheme}
                  />
                </IconWrapper>
              </Tooltip>
            )}
            {useInput ? (
              <InputField
                {...inputProps}
                ref={setRef}
                data-testid="text-input--input"
                hasRightIcon={!!props.rightIcon}
                isPasswordInput={props.isPasswordInput && !showPassword}
                isTextArea={props.isTextArea}
                maxLength={maxLength}
                placeholder={placeholder}
                spellCheck={props.spellCheck}
                theme={theme}
                type={props.type}
                value={currentValue}
                onChange={onInputChange}
              />
            ) : (
              <InputBox
                key={key.toString()}
                {...inputProps}
                ref={props.inputRef as React.RefObject<HTMLInputElement>}
                data-testid="text-input--editable-area"
                theme={theme}
                onKeyDown={handleEditableInput}
                onKeyUp={preventDefault}
              >
                {currentValue}
              </InputBox>
            )}
            {!props.error && props.icon && props.iconAtEnd && (
              <IconWrapper isEnd theme={theme}>
                <IconSwitcher
                  disabled={disabled}
                  height={theme.iconHeight}
                  icon={props.icon}
                  theme={iconTheme}
                />
              </IconWrapper>
            )}
            {!props.error && props.onRightButtonClick && (
              <ButtonWrapper theme={theme}>
                <Button
                  disabled={props.rightButtonDisabled}
                  height={theme.rightButtonHeight}
                  label={props.rightButtonLabel}
                  name="textInputRightButton"
                  theme={{ fontSize: theme.rightButtonFontsize }}
                  width={theme.rightButtonWidth}
                  onClick={props.onRightButtonClick}
                />
              </ButtonWrapper>
            )}
          </IconEventWrapper>
          <RightIcons data-testid="text-input--rights-icons">
            {props.rightIcon && (
              <>
                {props.onRightIconClick ? (
                  <IconButton
                    ariaLabel={props.rightIconLabel ?? ''}
                    height={theme.rightIconHeight}
                    icon={props.rightIcon}
                    name="textInputRightIconButton"
                    theme={props.rightIconTheme}
                    tooltipAlignment={props?.rightIconTooltipProps?.alignment}
                    tooltipPlacement={props?.rightIconTooltipProps?.placement}
                    tooltipText={props?.rightIconTooltipProps?.text}
                    onClick={props.onRightIconClick}
                  />
                ) : (
                  <IconSwitcher
                    height={theme.rightIconHeight}
                    icon={props.rightIcon}
                    theme={props.rightIconTheme}
                  />
                )}
              </>
            )}
            {props.isPasswordInput && (
              <button
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ cursor: 'pointer' }}
                type="button"
                onClick={togglePasswordVisibility}
              >
                <IconSwitcher
                  height="13px"
                  icon={showPassword ? EyeOutlineSlashIcon : EyeOutlineIcon}
                  theme={{
                    color: error ? theme.colorError : theme.passwordToggleColor,
                  }}
                />
              </button>
            )}
            {props.error && !props.disableErrorIcon && (
              <InfoThickIcon
                height="18px"
                theme={{
                  color: theme.colorError,
                  secondaryColor: theme.backgroundColor,
                }}
              />
            )}
          </RightIcons>
        </ContentWrapper>
      </InputContainer>
      {(props.infoText || error) && (
        <InfoText error={!!error} theme={theme}>
          {error || props.infoText}
        </InfoText>
      )}
    </InputWrapper>
  );
};

export default TextInput;
